/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Breadcrumbs,
  BreadcrumbsProps,
  Button,
  BUTTON_FORMAT_STRONG,
  BUTTON_LIST_POSITION_RIGHT,
  BUTTON_THEME_SUCCESS,
  ButtonList,
  ButtonProps,
  Divider,
  ICON_PLAY_CIRCLE_OUTLINE,
  Toast,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
} from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import { replaceTenantParam } from 'apps/console-core/src/Utilities/ReplaceTenantParam/ReplaceTenantParam';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { screenPaths } from '../../Constants/ScreenPaths.constants';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { SemanticAuditorTestBasicInfo } from './elements/SemanticAuditorTestBasicInfo/SemanticAuditorTestBasicInfo';
import { SemanticAuditorTestQuestions } from './elements/SemanticAuditorTestQuestions/SemanticAuditorTestQuestions';
import { SemanticAuditorTestResults } from './elements/SemanticAuditorTestResults/SemanticAuditorTestResults';
import { SemanticAuditorTestSummary } from './elements/SemanticAuditorTestSummary/SemanticAuditorTestSummary';
import { useSemanticAuditorTestBuilderStore } from './SemanticAuditorTestBuilder.state';

export const SemanticAuditorTestBuilder = (): JSX.Element => {
  const { t } = useTranslation();

  const labels = useMemo(() => {
    return {
      error: t(AI_STRINGS.error),
      // TODO: Add dynamic heading
      heading: t(`Lucinda's Jelly Bean Bonanza`),
      runTest: t(AI_STRINGS.runTest),
      save: t(AI_STRINGS.save),
      semanticAuditor: t(AI_STRINGS.semanticAuditor),
      testCreated: t(AI_STRINGS.testCreated),
      thereAreErrorsInTheForm: t(AI_STRINGS.thereAreErrorsInTheForm),
      youHaveSuccessFullyCreatedTest: t(AI_STRINGS.youHaveSuccessFullyCreatedTest),
    };
  }, [t]);
  const { validateForm, name } = useSemanticAuditorTestBuilderStore();
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const { updateActiveToast } = useToastStore();

  const breadcrumbProps: BreadcrumbsProps = {
    currentPageName: labels.heading,
    links: [
      ...capabilityBreadcrumbs,
      {
        name: labels.semanticAuditor,
        url: replaceTenantParam(screenPaths.semanticAuditor),
      },
    ],
  };
  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} />;

  // TODO: Complete save handler with an API call, extract toasts onto a separate hook with an API call.
  const onSaveHandler = async (): Promise<void> => {
    updateActiveToast(null);
    const isValid = await validateForm();
    if (!isValid) {
      return updateActiveToast(
        <Toast
          showToast
          stateHandler={useToastStore}
          title={labels.error}
          toastContent={<span>{labels.thereAreErrorsInTheForm}</span>}
          toastType={TOAST_TYPE_DANGER}
        />,
      );
    }
    updateActiveToast(
      <Toast
        showToast
        stateHandler={useToastStore}
        title={labels.testCreated}
        toastContent={
          <div
            dangerouslySetInnerHTML={{ __html: `${labels.youHaveSuccessFullyCreatedTest.replace('{{name}}', name)}` }}
          />
        }
        toastType={TOAST_TYPE_SUCCESS}
      />,
    );
  };

  // TODO: Add run test handler
  const onRunHandler = (): void => {
    // eslint-disable-next-line no-console
    console.log('Test was run');
  };

  const saveButtonProps: ButtonProps = {
    classes: 'semantic-auditor-test-builder__save-btn',
    format: BUTTON_FORMAT_STRONG,
    onClick: onSaveHandler,
    text: labels.save,
    theme: BUTTON_THEME_SUCCESS,
  };

  const runTestButtonProps: ButtonProps = {
    classes: 'semantic-auditor-test-builder__run-test-btn',
    icon: ICON_PLAY_CIRCLE_OUTLINE,
    onClick: onRunHandler,
    text: labels.runTest,
  };

  const screenContent = (
    <div className="semantic-auditor-test-builder">
      <div className="semantic-auditor-test-builder__header">
        <SemanticAuditorTestBasicInfo />
        <SemanticAuditorTestSummary />
      </div>
      <div className="semantic-auditor-test-builder__body">
        <SemanticAuditorTestResults />
        <SemanticAuditorTestQuestions />
      </div>
      <Divider />
      <ButtonList
        buttonKeys={['save']}
        buttons={[<Button {...saveButtonProps} key="save-btn" />]}
        buttonsPosition={BUTTON_LIST_POSITION_RIGHT}
        classes="semantic-auditor-test-builder__buttons"
      />
    </div>
  );

  return (
    <DxpScreen
      breadcrumbs={breadcrumbs}
      buttons={[<Button {...runTestButtonProps} key="run-test" />]}
      heading={labels.heading}
      showSeparator
    >
      {screenContent}
    </DxpScreen>
  );
};
